import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from '@/contexts/auth';
import { useOptimizedAssistantManager } from '@/hooks/ai-agents/state/useOptimizedAssistantManager';
import { CopilotAssistant } from '@/hooks/ai-agents/types/database-types';
import { useUserProfile } from '@/hooks/profile/useUserProfile';

/**
 * AI Agent Data Context - Optimized context specifically for AI Agent system
 * Provides granular state slicing to prevent unnecessary re-renders
 */

// Base AI Agent state interface
export interface AIAgentDataState {
  // Assistant slice
  assistants: {
    data: CopilotAssistant[];
    active: CopilotAssistant | null;
    activeId: string | null;
    isLoading: boolean;
    error: string | null;
    setActiveId: (id: string | null) => void;
  };
  
  // Chat slice
  chat: {
    isReady: boolean;
    threadCount: number;
    functionCount: number;
  };
  
  // User slice
  user: {
    id: string | null;
    isAuthenticated: boolean;
    preferences: any;
    profile: any;
  };
}

const AIAgentDataContext = createContext<AIAgentDataState | null>(null);

// Optimized selector hooks for AI Agent system
export const useAssistantsSlice = () => {
  const context = useContext(AIAgentDataContext);
  if (!context) throw new Error('useAssistantsSlice must be used within AIAgentDataProvider');
  return context.assistants;
};

export const useChatSlice = () => {
  const context = useContext(AIAgentDataContext);
  if (!context) throw new Error('useChatSlice must be used within AIAgentDataProvider');
  return context.chat;
};

export const useAIAgentUserSlice = () => {
  const context = useContext(AIAgentDataContext);
  if (!context) throw new Error('useAIAgentUserSlice must be used within AIAgentDataProvider');
  return context.user;
};

// Provider component
interface AIAgentDataProviderProps {
  children: React.ReactNode;
}

export const AIAgentDataProvider: React.FC<AIAgentDataProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { profile: profileData } = useUserProfile();
  
  // Get optimized assistant data
  const {
    assistants,
    activeAssistantId,
    setActiveAssistantId,
    currentAssistant,
    error,
    isLoading,
    isInitialized,
    dashboardData
  } = useOptimizedAssistantManager(user?.id);

  // Memoized assistants slice
  const assistantsSlice = useMemo(() => ({
    data: assistants,
    active: currentAssistant,
    activeId: activeAssistantId,
    isLoading,
    error,
    setActiveId: setActiveAssistantId,
  }), [assistants, currentAssistant, activeAssistantId, isLoading, error, setActiveAssistantId]);

  // Memoized chat slice
  const chatSlice = useMemo(() => ({
    isReady: isInitialized && !!user && assistants.length > 0,
    threadCount: dashboardData.threadCount,
    functionCount: dashboardData.functionCount,
  }), [isInitialized, user, assistants.length, dashboardData.threadCount, dashboardData.functionCount]);

  // Memoized user slice
  const userSlice = useMemo(() => ({
    id: user?.id || null,
    isAuthenticated,
    preferences: dashboardData.userPreferences,
    profile: profileData,
  }), [user?.id, isAuthenticated, dashboardData.userPreferences, profileData]);

  // Memoized context value
  const contextValue = useMemo((): AIAgentDataState => ({
    assistants: assistantsSlice,
    chat: chatSlice,
    user: userSlice,
  }), [assistantsSlice, chatSlice, userSlice]);

  return (
    <AIAgentDataContext.Provider value={contextValue}>
      {children}
    </AIAgentDataContext.Provider>
  );
};

// Main hook for accessing AI Agent data
export const useAIAgentData = () => {
  const context = useContext(AIAgentDataContext);
  if (!context) {
    throw new Error('useAIAgentData must be used within an AIAgentDataProvider');
  }
  return context;
};

// Performance-optimized HOC for AI Agent components
export const withAIAgentData = <P extends object>(
  Component: React.ComponentType<P>,
  selector?: (state: AIAgentDataState) => any
) => {
  return React.memo((props: P) => {
    const aiAgentData = useAIAgentData();
    const selectedData = selector ? selector(aiAgentData) : aiAgentData;
    
    return <Component {...props} aiAgentData={selectedData} />;
  });
};

// Optimized selectors for common use cases
export const selectAssistantOverview = (state: AIAgentDataState) => ({
  assistants: state.assistants.data,
  activeAssistant: state.assistants.active,
  activeAssistantId: state.assistants.activeId,
  isLoading: state.assistants.isLoading,
});

export const selectChatStatus = (state: AIAgentDataState) => ({
  isReady: state.chat.isReady,
  threadCount: state.chat.threadCount,
  isAuthenticated: state.user.isAuthenticated,
});

export const selectUserContext = (state: AIAgentDataState) => ({
  userId: state.user.id,
  isAuthenticated: state.user.isAuthenticated,
  preferences: state.user.preferences,
});

export default AIAgentDataProvider;
